import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL, assetUrl } from "../config/api";
import { PROJECTS } from "../data/projects";
import { SERVICE_CATEGORY_BY_SLUG } from "../data/services";

/**
 * Projects for the public site.
 *
 * Starts from the curated set in data/projects.js and swaps in the live CMS
 * list once it arrives. That ordering matters:
 *
 *  • The page renders complete on first paint — no empty portfolio flash, no
 *    layout shift when the request lands.
 *  • If the API is unreachable, or the production database hasn't been seeded
 *    yet, the portfolio still shows eight real projects instead of nothing.
 *  • Once the owner adds projects through the admin dashboard, those become
 *    the source of truth automatically.
 *
 * An empty-but-successful API response is treated as "nothing published yet"
 * and leaves the fallback in place, rather than blanking the page.
 */

const slugForCategory = (category) =>
  Object.keys(SERVICE_CATEGORY_BY_SLUG).find(
    (slug) => SERVICE_CATEGORY_BY_SLUG[slug] === category
  );

/**
 * Shapes a CMS row like a data/projects.js entry, including media objects that
 * <Figure> can render directly.
 *
 * The CMS has no per-image alt text field, so alt is derived from the project
 * title. That's weaker than the hand-written alt in data/media.js — it
 * identifies the photo without describing it — but it's accurate, which a
 * generic "project image" would not be. Worth adding an alt field to the
 * upload form if the portfolio grows.
 */
const fromApi = (project) => ({
  id: String(project.id),
  title: project.title,
  category: project.category,
  serviceSlug: slugForCategory(project.category),
  summary: project.description,
  description: project.description,
  images: (project.image_paths || []).map((path, index) => ({
    src: assetUrl(path),
    ratio: "4 / 5",
    caption: project.title,
    alt: `${project.title} — photograph ${index + 1} of a Five Lights Reno project`,
  })),
});

export default function useProjects() {
  const [projects, setProjects] = useState(PROJECTS);

  useEffect(() => {
    let cancelled = false;

    axios
      .get(`${API_BASE_URL}/portfolio_list.php`)
      .then((response) => {
        if (cancelled) return;
        const live = response.data?.projects;
        if (!response.data?.success || !Array.isArray(live) || live.length === 0) return;

        setProjects(live.map(fromApi).filter((project) => project.images.length > 0));
      })
      .catch(() => {
        // Leave the curated fallback in place. A portfolio page is not the
        // right surface for an infrastructure error message.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return projects;
}
